import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import { X, KeyRound, Mail, Copy, Loader2 } from "lucide-react";

export default function PortalPasswordDialog({ client, onClose }) {
  const [users, setUsers] = useState([client.portal_user]);
  const [selId, setSelId] = useState(client.portal_user.client_user_id);
  const [busy, setBusy] = useState(null);
  const [temp, setTemp] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/clients/${client.client_id}/users`).then((r) => r.data?.length && setUsers(
      r.data.some((u) => u.client_user_id === client.portal_user.client_user_id) ? r.data : [client.portal_user, ...r.data])).catch(() => {});
  }, [client.client_id, client.portal_user]);
  const pu = users.find((u) => u.client_user_id === selId) || users[0];
  const base = `${API_BASE}/clients/${client.client_id}/users/${pu.client_user_id}`;

  const run = async (kind) => {
    if (kind === "temp" && !window.confirm(`Replace ${pu.email}'s current password with a temporary one?`)) return;
    setBusy(kind);
    try {
      const { data } = await axios.post(`${base}/${kind === "email" ? "send-reset" : "temp-password"}`);
      if (kind === "email") { toast.success(`Reset link emailed to ${data.email}`); onClose(); }
      else setTemp(data.temp_password);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="portal-password-dialog">
      <div className="bg-[var(--paper)] rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[14px] font-medium"><KeyRound size={15} /> Portal password · {client.name}</div>
          <button onClick={onClose} data-testid="portal-password-close"><X size={17} /></button>
        </div>
        <div className="p-5 space-y-4">
          {users.length > 1 && !temp && (
            <select value={pu.client_user_id} onChange={(e) => setSelId(e.target.value)} data-testid="portal-password-user-select"
                    className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] font-mono">
              {users.map((u) => <option key={u.client_user_id} value={u.client_user_id}>{u.email}{u.name ? ` — ${u.name}` : ""}</option>)}
            </select>
          )}
          <div className="text-[12px] text-[var(--ink-3)]">
            Portal user <span className="font-mono text-[var(--ink)]" data-testid="portal-password-user-email">{pu.email}</span>
            {pu.has_password ? " · has a password" : " · no password set yet"}
            {pu.must_change_password ? " · temporary password pending change" : ""}
          </div>
          {temp ? (
            <div className="space-y-2" data-testid="portal-temp-result">
              <div className="overline">Temporary password — shown once</div>
              <div className="flex gap-2">
                <code className="flex-1 bg-[var(--subtle)] rounded px-3 py-2 font-mono text-[15px] tracking-wider" data-testid="portal-temp-password">{temp}</code>
                <button className="btn-secondary" data-testid="portal-temp-copy"
                  onClick={() => navigator.clipboard.writeText(temp).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed — select it manually"))}>
                  <Copy size={13} /></button>
              </div>
              <p className="text-[12px] text-[var(--ink-2)]">Share it securely (e.g. by phone). The client must choose a new password when they next sign in.</p>
              <button className="btn-primary w-full justify-center" onClick={onClose} data-testid="portal-temp-done">Done</button>
            </div>
          ) : (
            <>
              <button className="card p-4 w-full text-left hover:border-[var(--ink)] transition-colors" disabled={!!busy}
                      onClick={() => run("email")} data-testid="portal-send-reset">
                <div className="flex items-center gap-2 text-[13px] font-medium">{busy === "email" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email a reset link</div>
                <div className="text-[12px] text-[var(--ink-3)] mt-1">Client gets a single-use link (valid 60 minutes) to choose a new password.</div>
              </button>
              <button className="card p-4 w-full text-left hover:border-[var(--ink)] transition-colors" disabled={!!busy}
                      onClick={() => run("temp")} data-testid="portal-set-temp">
                <div className="flex items-center gap-2 text-[13px] font-medium">{busy === "temp" ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Set a temporary password</div>
                <div className="text-[12px] text-[var(--ink-3)] mt-1">You pass it on yourself; they must change it on next sign-in.</div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
